// Sentry must initialize before any React code mounts so route-change
// instrumentation can hook into the router from the very first render.
import { initSentry, captureException } from "./sentry";
initSentry();

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { Provider } from "react-redux";
import { store } from "./store/store";
import Toaster from "./components/notifications/Toaster";
import { AuthProvider } from "./contexts/AuthContext";
import { TourProvider } from "./contexts/TourContext";
import ErrorBoundary from "./components/ErrorBoundary";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

// Test user helpers available in console (type setTestAdmin() etc)
// Only load in development
if (import.meta.env.DEV) {
  import("./utils/testUserHelper.js");
}

// Global error handlers — log locally AND forward to Sentry so we see them
// in production. Sentry also installs its own window.onerror hook but we
// keep these so the console.error calls (used for debugging in dev) still fire.
window.onerror = function (message, source, lineno, colno, error) {
  console.error("Global error caught:", { message, source, lineno, colno, error });
  if (error) captureException(error, { source, lineno, colno });
  return false;
};

window.onunhandledrejection = function (event) {
  console.error("Unhandled promise rejection:", event.reason);
  if (event.reason) captureException(event.reason, { kind: "unhandledrejection" });
};

createRoot(document.getElementById("root")).render(
  <StrictMode>
    <ErrorBoundary>
      <Provider store={store}>
        <AuthProvider>
          <TourProvider>
            <Toaster />
            <App />
            <PWAInstallPrompt />
          </TourProvider>
        </AuthProvider>
      </Provider>
    </ErrorBoundary>
  </StrictMode>
);
